import { Type } from '@angular/core'
import {
  LucideAppWindow,
  LucideBox,
  LucideChartLine,
  LucideChartNoAxesGantt,
  LucideCircleAlert,
  LucideCloudUpload,
  LucideFlaskConical,
  LucideHistory,
  LucideIcon,
  LucideLayers,
  LucideMegaphone,
  LucideMessageSquare,
  LucideMonitor,
  LucideReceipt,
} from '@lucide/angular'
import { ShowcaseBannersComponent } from './sections/banners/showcase-banners.component'
import { ShowcaseBackupScreenComponent } from './sections/backup-screen/showcase-backup-screen.component'
import { ShowcaseChartComponent } from './sections/chart/showcase-chart.component'
import { ShowcaseErrorsComponent } from './sections/errors/showcase-errors.component'
import { ShowcaseIssueScenariosComponent } from './sections/issue-scenarios/showcase-issue-scenarios.component'
import { ShowcaseIssuesComponent } from './sections/issues/showcase-issues.component'
import { ShowcaseLegacyTemplatesComponent } from './sections/legacy-templates/showcase-legacy-templates.component'
import { ShowcaseModalsComponent } from './sections/modals/showcase-modals.component'
import { ShowcaseRecoveryScreenComponent } from './sections/recovery-screen/showcase-recovery-screen.component'
import { ShowcaseResourcesComponent } from './sections/resources/showcase-resources.component'
import { ShowcaseRetentionComponent } from './sections/retention/showcase-retention.component'
import { ShowcaseStacksetsComponent } from './sections/stacksets/showcase-stacksets.component'
import { ShowcaseTabStatusComponent } from './sections/tab-status/showcase-tab-status.component'
import { ShowcaseTooltipsComponent } from './sections/tooltips/showcase-tooltips.component'
import { ShowcaseUsageBillingComponent } from './sections/usage-billing/showcase-usage-billing.component'

export interface ShowcaseSection {
  /** Accordion expand-state key, also the anchor stem. */
  key: string
  /** DOM id of the shell-rendered <section>; scrollTo() resolves it with getElementById. */
  anchor: string
  /** Nav-pill text. */
  label: string
  /** Lucide glyph component for both the pill and the section heading. */
  icon: LucideIcon
  /** Accordion heading. */
  title: string
  component: Type<unknown>
}

// Adding a showcase is a new folder under sections/ plus one entry here and one
// declarations line in ArpioModule.
const SECTIONS: ReadonlyArray<Omit<ShowcaseSection, 'anchor'>> = [
  {
    key: 'issues',
    label: 'Issue Types',
    icon: LucideCircleAlert,
    title: 'All Issue Types',
    component: ShowcaseIssuesComponent,
  },
  {
    key: 'issuesScenarios',
    label: 'Scenarios',
    icon: LucideFlaskConical,
    title: 'Issues Scenarios',
    component: ShowcaseIssueScenariosComponent,
  },
  {
    key: 'modals',
    label: 'Modals',
    icon: LucideAppWindow,
    title: 'Modals',
    component: ShowcaseModalsComponent,
  },
  {
    key: 'resources',
    label: 'Resources',
    icon: LucideBox,
    title: 'Resource Status',
    component: ShowcaseResourcesComponent,
  },
  {
    key: 'recoveryScreen',
    label: 'Recovery',
    icon: LucideMonitor,
    title: 'Recovery screens',
    component: ShowcaseRecoveryScreenComponent,
  },
  {
    key: 'backupScreen',
    label: 'Backup',
    icon: LucideCloudUpload,
    title: 'Backup screens',
    component: ShowcaseBackupScreenComponent,
  },
  {
    key: 'banners',
    label: 'Banners',
    icon: LucideMegaphone,
    title: 'App-Wide Banners',
    component: ShowcaseBannersComponent,
  },
  {
    key: 'tabStatus',
    label: 'Tab Status',
    icon: LucideChartNoAxesGantt,
    title: 'Tab Status Bar',
    component: ShowcaseTabStatusComponent,
  },
  {
    key: 'retention',
    label: 'Retention',
    icon: LucideHistory,
    title: 'Retention Policy',
    component: ShowcaseRetentionComponent,
  },
  {
    key: 'chart',
    label: 'Chart',
    icon: LucideChartLine,
    title: 'RPO Chart',
    component: ShowcaseChartComponent,
  },
  {
    key: 'stacksets',
    label: 'StackSets',
    icon: LucideLayers,
    title: 'StackSets Scenarios',
    component: ShowcaseStacksetsComponent,
  },
  {
    key: 'usageBilling',
    label: 'Usage',
    icon: LucideReceipt,
    title: 'Usage & Billing',
    component: ShowcaseUsageBillingComponent,
  },
  {
    key: 'legacyTemplates',
    label: 'Legacy Templates',
    icon: LucideCircleAlert,
    title: 'Outdated Access Templates',
    component: ShowcaseLegacyTemplatesComponent,
  },
  {
    key: 'errors',
    label: 'Errors',
    icon: LucideCircleAlert,
    title: 'Error System',
    component: ShowcaseErrorsComponent,
  },
  {
    key: 'tooltips',
    label: 'Tooltips',
    icon: LucideMessageSquare,
    title: 'Tooltips',
    component: ShowcaseTooltipsComponent,
  },
]

// The anchor is derived rather than stored so the id and the expand-state key can
// never drift apart. Every existing section id already matches this shape, so no
// deep link changes.
export const SHOWCASE_SECTIONS: ReadonlyArray<ShowcaseSection> = SECTIONS.map((s) => ({
  ...s,
  anchor: `${s.key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}-section`,
}))
